import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { clearUser } from 'store/slices/user'
import { clearToken } from 'store/slices/auth'
import { MenuItemProps } from 'interfaces/menu'
import MenuItem from 'components/atoms/MenuItem/MenuItem'
import { RootState } from 'store/store'
import { isAdminRole } from 'users/domain/IRoleRepository'

interface UserProfile {
    name: string
    role: string
    avatar?: string
}

interface Props {
    data: (MenuItemProps & { icon?: string; category?: string })[]
    user?: UserProfile
    isCollapsed?: boolean
}

export default function MenuList({ data, user, isCollapsed = false }: Props) {
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const userState = useSelector((state: RootState) => state.user)
    const storedUser = userState?.user

    const logoutHandle = () => {
        dispatch(clearUser())
        dispatch(clearToken())
        navigate('/')
    }

    const configuratorItems = data.filter(
        (item) => item.category === 'configurator'
    )
    const operatorItems = data.filter((item) => item.category === 'operator')
    const otherItems = data.filter((item) => !item.category)

    const renderMenuItems = (items: typeof data) => {
        return items.map((item, index) => (
            <MenuItem
                key={index}
                title={item.title}
                url={item.url}
                state={item.state}
                icon={item.icon}
                active={location.pathname.includes(item.url)}
                isCollapsed={isCollapsed}
                isLastItem={item.id === 'logout'}
                onClick={(url) => {
                    if (url === 'logout') {
                        logoutHandle()
                    } else {
                        navigate('/' + url)
                    }
                }}
            />
        ))
    }

    return (
        <div className="menu-list bg-white flex flex-col h-full">
            <div className="flex-1 overflow-y-auto">
                {configuratorItems.length > 0 &&
                    storedUser?.role?.name &&
                    isAdminRole(storedUser.role.name) && (
                        <div className="mb-6">
                            {!isCollapsed && (
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                                    Configuración
                                </h3>
                            )}
                            <div
                                className={`space-y-2 ${isCollapsed ? 'flex flex-col items-center' : ''}`}
                            >
                                {renderMenuItems(configuratorItems)}
                            </div>
                        </div>
                    )}
                {operatorItems.length > 0 && (
                    <div className="mb-6">
                        {!isCollapsed && (
                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                                Operación
                            </h3>
                        )}
                        <div
                            className={`space-y-2 ${isCollapsed ? 'flex flex-col items-center' : ''}`}
                        >
                            {renderMenuItems(operatorItems)}
                        </div>
                    </div>
                )}
            </div>

            <div className="border-t border-gray-200 mt-auto absolute bottom-0 w-full bg-white top-auto left-0 py-5 h-[112px]">
                <div
                    className={`flex items-center gap-3 px-5 ${isCollapsed ? 'justify-center' : ''}`}
                >
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex-shrink-0 overflow-hidden flex items-center justify-center">
                        {user?.avatar ? (
                            <img
                                src={''}
                                alt={storedUser?.name || 'Usuario'}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <span className="text-gray-600 font-semibold text-sm">
                                {user?.name
                                    .split(' ')
                                    .map((n) => n[0])
                                    .join('')
                                    .toUpperCase() || 'US'}
                            </span>
                        )}
                    </div>
                    {!isCollapsed && (
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 text-sm truncate">
                                {storedUser?.name || 'Usuario'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                                {storedUser?.role
                                    ? String(storedUser.role.name)
                                    : 'Rol'}
                            </p>
                        </div>
                    )}
                </div>
                {otherItems.length > 0 && (
                    <div className={`space-y-2 px-5 `}>
                        {renderMenuItems(otherItems)}
                    </div>
                )}
            </div>
        </div>
    )
}
